import { useContext, createContext, useRef, useEffect, createElement } from 'react';
import { useRouter } from 'next/compat/router';

function getEventCallbacksByPriority(events = {}) {
  const eventsByPriority = Object.values(events).reduce((acc, event = {}) => {
    // assume all events are unassigned
    const {
      priority = 'unassigned'
    } = event;

    // create priority collection if one doesn't exist
    if (!Array.isArray(acc[priority])) {
      acc[priority] = [];
    }
    acc[priority].push(event);
    return acc;
  }, {
    unassigned: []
  });
  return eventsByPriority;
}
async function processEventOptions(callbacks, ...args) {
  const prioritizedEventGroups = getEventCallbacksByPriority(callbacks.current);
  const eventGroups = Object.values(prioritizedEventGroups);

  // call each eventGroup in serial but all callbacks of each eventGroup in parallel
  // eslint-disable-next-line no-restricted-syntax
  for (const eventGroup of eventGroups) {
    const callbackPromises = eventGroup.map(async ({
      callback
    }) => callback(...args));
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(callbackPromises);
  }
}
function getInitialUrl(basePath, asPath) {
  // if there is a basePath then add it and remove any trailing slash
  if (basePath) {
    return basePath + asPath.replace(/\/$/, '');
  }
  return asPath;
}
const PageEventContext = /*#__PURE__*/createContext({
  setPageEnter: () => {},
  setPageError: () => {},
  setPageExit: () => {},
  isServerRendered: true
});
function PageEventProvider(props) {
  const isServerRendered = useRef(true);
  const pageEnterEvents = useRef({});
  const pageExitEvents = useRef({});
  const pageErrorEvents = useRef({});
  const router = useRouter();
  function setPageEnter({
    callback,
    key,
    options = {}
  }) {
    if (pageEnterEvents.current) {
      pageEnterEvents.current[key + '_enter'] = {
        ...options,
        key,
        callback
      };
    }
  }
  function setPageError({
    callback,
    key,
    options = {}
  }) {
    pageErrorEvents.current[key + '_error'] = {
      ...options,
      key,
      callback
    };
  }
  function setPageExit({
    callback,
    key,
    options = {}
  }) {
    pageExitEvents.current[key + '_exit'] = {
      ...options,
      key,
      callback
    };
  }
  const handleEnter = async url => {
    await processEventOptions(pageEnterEvents, url);

    // clear/disable the pageEnterEvents queue until the next page
    pageEnterEvents.current = undefined;
  };
  const handleExit = async url => {
    await processEventOptions(pageExitEvents, url);
    pageExitEvents.current = {};
    // clear the pageErrorEvents/pageExitEvents queue until the next page
    pageErrorEvents.current = {};
    pageExitEvents.current = {};
  };
  const handleError = async (err, url) => {
    await processEventOptions(pageErrorEvents, err, url);
  };
  const handleBeforeHistoryChange = () => {
    // re-enable the pageEnterEventss before the next page renders
    pageEnterEvents.current = {};
  };
  useEffect(() => {
    if (router && !router.isReady) {
      return;
    }
    const {
      basePath = '',
      asPath,
      events
    } = router;

    // we must fire the initial onPageEnter on mount of the component as routeChangeComplete does not fire on initial render
    // it is removed from subsequent renders and client side routing mounts via the isServerRendered ref
    // https://github.com/vercel/next.js/issues/4662
    if (isServerRendered.current) {
      (async () => {
        // We must recreate the URL argument that is passed to the routeChangeComplete callback
        const initialUrl = getInitialUrl(basePath, asPath);
        await handleEnter(initialUrl);

        // flag that all subsequent pages are client rendered
        isServerRendered.current = false;
      })();
    }
    events.on('routeChangeStart', handleExit);
    events.on('routeChangeComplete', handleEnter);
    events.on('routeChangeError', handleError);
    events.on('beforeHistoryChange', handleBeforeHistoryChange);

    // eslint-disable-next-line consistent-return
    return () => {
      events.off('routeChangeStart', handleExit);
      events.off('routeChangeComplete', handleEnter);
      events.off('routeChangeError', handleError);
      events.off('beforeHistoryChange', handleBeforeHistoryChange);
    };
  }, [router]);
  return /*#__PURE__*/createElement(PageEventContext.Provider, {
    ...props,
    value: {
      setPageEnter,
      setPageError,
      setPageExit,
      isServerRendered
    }
  });
}
function usePageEvent(key, options) {
  if (!key) {
    throw new Error('usePageEvent requires a key to be passed.  No key has been supplied');
  }
  const pageEventContext = useContext(PageEventContext);
  return {
    onPageEnter: callback => pageEventContext.setPageEnter({
      callback,
      key,
      options
    }),
    onPageError: callback => pageEventContext.setPageError({
      callback,
      key,
      options
    }),
    onPageExit: callback => pageEventContext.setPageExit({
      callback,
      key,
      options
    }),
    isServerRendered: pageEventContext.isServerRendered
  };
}

export { PageEventContext, PageEventProvider, getEventCallbacksByPriority, getInitialUrl, processEventOptions, usePageEvent };
